package com.siriusxm.pia.views.favorites

import androidx.compose.runtime.*
import com.siriusxm.pia.Application
import com.siriusxm.pia.rest.unifiedaggregator.asEntities
import com.siriusxm.pia.rest.unifiedaggregator.unifiedAggregatorClient
import com.siriusxm.unifiedcontent.cmc.AddFavoritePayload
import com.siriusxm.unifiedcontent.cmc.CMCService
import io.ktor.client.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.serialization.kotlinx.json.*
import io.ktor.utils.io.*
import kotlinx.browser.localStorage
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlin.time.Clock
import kotlin.time.Duration.Companion.minutes

/**
 * Simplified entity data for caching in localStorage.
 */
@Serializable
data class CachedEntity(
    val id: String,
    val type: String,
    val name: String,
    val thumbnailUrl: String?
)

/**
 * Global state manager for user favorites.
 *
 * This singleton manages the user's favorited entities across the application.
 * It provides methods to add, remove, and check favorites, as well as load
 * the full list of favorited entities.
 *
 * Usage:
 * ```kotlin
 * @Composable
 * fun MyComponent() {
 *     val favorites = rememberFavorites()
 *     val isFavorite = favorites.isFavorite("show:12345")
 *
 *     Button(onClick = { favorites.toggleFavorite("show:12345") }) {
 *         Text(if (isFavorite) "Remove from Favorites" else "Add to Favorites")
 *     }
 * }
 * ```
 */
class FavoritesState {
    private var cmcService: CMCService? = null
    private var lastServerLoad: Long = 0L
    private val json = Json { ignoreUnknownKeys = true }

    /**
     * Set of entity IDs that are currently favorited.
     * Entity IDs are in the format "<type>:<id>" (e.g., "show:12345").
     */
    val favoritedIds = mutableStateOf<Set<String>>(emptySet())

    /**
     * Cached entity data for display. Loaded from localStorage immediately,
     * then refreshed from server in background.
     */
    val cachedEntities = mutableStateOf<List<CachedEntity>>(emptyList())

    /**
     * Whether favorites are currently being loaded from the server.
     */
    val isLoading = mutableStateOf(false)

    /**
     * Whether favorites have been loaded at least once (from cache or server).
     */
    val isInitialized = mutableStateOf(false)

    /**
     * Initializes the favorites state with the CMC service client.
     * Immediately loads from local storage cache if available.
     * Should be called once when the application starts.
     */
    fun initialize(cmcApiUrl: String, installAuth: (HttpClientConfig<*>) -> Unit) {
        if (cmcService != null) return

        // Load from cache immediately
        loadFromCache()

        val httpClient = HttpClient {
            install(ContentNegotiation) {
                json(Json {
                    isLenient = true
                    ignoreUnknownKeys = true
                })
            }
            expectSuccess = true
            installAuth(this)
        }
        cmcService = CMCService(cmcApiUrl, httpClient)
    }

    /**
     * Loads favorites from local storage cache.
     * Called synchronously during initialization for immediate display.
     */
    private fun loadFromCache() {
        try {
            val cached = localStorage.getItem(CACHE_KEY)
            val timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY)?.toLongOrNull() ?: 0L
            val entitiesJson = localStorage.getItem(CACHE_ENTITIES_KEY)

            if (cached != null) {
                val ids = cached.split(",").filter { it.isNotBlank() }.toSet()
                favoritedIds.value = ids
                lastServerLoad = timestamp
                isInitialized.value = true

                // Load cached entity data if available
                if (entitiesJson != null) {
                    try {
                        cachedEntities.value = json.decodeFromString<List<CachedEntity>>(entitiesJson)
                    } catch (e: Exception) {
                        console.error("Failed to parse cached entities", e)
                    }
                }
            }
        } catch (e: Exception) {
            console.error("Failed to load favorites from cache", e)
        }
    }

    /**
     * Saves favorites to local storage cache.
     */
    private fun saveToCache() {
        try {
            val data = favoritedIds.value.joinToString(",")
            localStorage.setItem(CACHE_KEY, data)
            localStorage.setItem(CACHE_TIMESTAMP_KEY, lastServerLoad.toString())

            // Save entity data
            if (cachedEntities.value.isNotEmpty()) {
                localStorage.setItem(CACHE_ENTITIES_KEY, json.encodeToString(cachedEntities.value))
            }
        } catch (e: Exception) {
            console.error("Failed to save favorites to cache", e)
        }
    }

    /**
     * Clears the cache timestamp to force a reload on next loadFavorites call.
     */
    private fun invalidateCache() {
        lastServerLoad = 0L
        saveToCache()
    }

    /**
     * Checks if the cache is still valid (less than 10 minutes old).
     */
    private fun isCacheValid(): Boolean {
        val now = Clock.System.now().toEpochMilliseconds()
        return (now - lastServerLoad) < CACHE_DURATION.inWholeMilliseconds
    }

    /**
     * Loads the user's favorites from the backend and hydrates entity data.
     * Skips server call if cache is still valid (less than 10 minutes old).
     * This should be called when the application starts or when favorites need to be refreshed.
     */
    suspend fun loadFavorites() {
        val service = cmcService ?: return

        // Skip if cache is still valid and we have entity data
        if (isCacheValid() && isInitialized.value && cachedEntities.value.isNotEmpty()) {
            return
        }

        isLoading.value = true
        try {
            val favorites = service.favorites.getFavorites()
            favoritedIds.value = favorites.toSet()

            // Hydrate entity data
            if (favorites.isNotEmpty()) {
                hydrateEntities(favorites)
            } else {
                cachedEntities.value = emptyList()
            }

            lastServerLoad = Clock.System.now().toEpochMilliseconds()
            isInitialized.value = true
            saveToCache()
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            console.error("Failed to load favorites", e)
            // If we have cached data, keep using it
            if (!isInitialized.value) {
                isInitialized.value = favoritedIds.value.isNotEmpty()
            }
        } finally {
            isLoading.value = false
        }
    }

    /**
     * Fetches full entity data for the given favorite IDs and updates cachedEntities.
     */
    private suspend fun hydrateEntities(favoriteIds: List<String>) {
        try {
            val client = unifiedAggregatorClient(Application.context)

            val entities = coroutineScope {
                favoriteIds.map { entityId ->
                    async {
                        try {
                            val parsed = parseEntityId(entityId)
                            if (parsed != null) {
                                val (type, id) = parsed
                                val results = client.fetchEntityById(id)
                                results.asEntities().firstOrNull { it.type == type }?.let { entity ->
                                    CachedEntity(
                                        id = entity.id,
                                        type = entity.type,
                                        name = entity.label,
                                        thumbnailUrl = entity.thumbnail?.url
                                    )
                                }
                            } else {
                                null
                            }
                        } catch (e: Exception) {
                            console.error("Failed to fetch entity: $entityId", e)
                            null
                        }
                    }
                }.awaitAll().filterNotNull()
            }

            cachedEntities.value = entities
        } catch (e: Exception) {
            console.error("Failed to hydrate entities", e)
        }
    }

    /**
     * Checks if an entity is in the user's favorites.
     *
     * @param entityId The entity reference in format "<type>:<id>"
     * @return true if the entity is favorited
     */
    fun isFavorite(entityId: String): Boolean {
        return entityId in favoritedIds.value
    }

    /**
     * Adds an entity to the user's favorites.
     * Updates local state optimistically before the API call completes.
     * Clears the cache to ensure fresh data on next load.
     *
     * @param entityId The entity reference in format "<type>:<id>"
     */
    suspend fun addFavorite(entityId: String) {
        val service = cmcService ?: return

        // Optimistic update
        favoritedIds.value = favoritedIds.value + entityId
        invalidateCache()
        saveToCache()

        try {
            service.favorites.addFavorite(AddFavoritePayload(entityId))
        } catch (e: Exception) {
            console.error("Failed to add favorite", e)
            // Rollback on failure
            favoritedIds.value = favoritedIds.value - entityId
            saveToCache()
        }
    }

    /**
     * Removes an entity from the user's favorites.
     * Updates local state optimistically before the API call completes.
     * Clears the cache to ensure fresh data on next load.
     *
     * @param entityId The entity reference in format "<type>:<id>"
     */
    suspend fun removeFavorite(entityId: String) {
        val service = cmcService ?: return

        // Optimistic update
        favoritedIds.value = favoritedIds.value - entityId
        invalidateCache()
        saveToCache()

        try {
            service.favorites.removeFavorite(entityId)
        } catch (e: Exception) {
            console.error("Failed to remove favorite", e)
            // Rollback on failure
            favoritedIds.value = favoritedIds.value + entityId
            saveToCache()
        }
    }

    /**
     * Toggles the favorite status of an entity.
     *
     * @param entityId The entity reference in format "<type>:<id>"
     */
    suspend fun toggleFavorite(entityId: String) {
        if (isFavorite(entityId)) {
            removeFavorite(entityId)
        } else {
            addFavorite(entityId)
        }
    }

    companion object {
        private const val CACHE_KEY = "favorites_cache"
        private const val CACHE_TIMESTAMP_KEY = "favorites_cache_timestamp"
        private const val CACHE_ENTITIES_KEY = "favorites_entities_cache"
        private val CACHE_DURATION = 10.minutes

        /**
         * Singleton instance of FavoritesState.
         */
        val instance = FavoritesState()
    }
}

/**
 * CompositionLocal providing access to the FavoritesState.
 */
val LocalFavoritesState: ProvidableCompositionLocal<FavoritesState> =
    staticCompositionLocalOf { FavoritesState.instance }

/**
 * Composable function to access the favorites state.
 * Automatically initializes and loads favorites on first access.
 *
 * @return The FavoritesState instance
 */
@Composable
fun rememberFavorites(): FavoritesState {
    val state = LocalFavoritesState.current

    // Initialize and load favorites
    LaunchedEffect(Unit) {
        val cmcApiUrl = Application.configuration.cmcApiUrl
        if (cmcApiUrl != null) {
            state.initialize(cmcApiUrl) { config ->
                Application.context.installAuth(config)
            }
            // Always call loadFavorites - it will check cache validity internally
            state.loadFavorites()
        }
    }

    return state
}

/**
 * Creates an entity ID string from type and id components.
 *
 * @param type The entity type (e.g., "show", "channel")
 * @param id The entity ID
 * @return The combined entity reference string "<type>:<id>"
 */
fun createEntityId(type: String, id: String): String = "$type:$id"

/**
 * Parses an entity ID string into type and id components.
 *
 * @param entityId The entity reference in format "<type>:<id>"
 * @return Pair of (type, id) or null if the format is invalid
 */
fun parseEntityId(entityId: String): Pair<String, String>? {
    val parts = entityId.split(":", limit = 2)
    return if (parts.size == 2) {
        parts[0] to parts[1]
    } else {
        null
    }
}
